var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import debug from "debug";
import ky from "ky";
import { OriginModeEnum, WIDGET_CONVERSATION_TYPE, WIDGET_MODE, WIDGET_REFERRER_ID, ErrorCode, } from "./models";
import { EventService } from "./genesys.chat.api.event.service";
import { codeError } from "./utils";
import { Restorable } from "./models/Restorable";
const log = debug("auvious:integrations");
export class GenesysChatApiService extends Restorable {
    get prefixUrl() {
        // return `${API_SOURCE_MAP[this.config.pcEnvironment]}/${this.API_VERSION}/`;
        return `https://api.${this.config.pcEnvironment}/${this.API_VERSION}/`;
    }
    constructor(config, handlers, notifyReady) {
        var _a, _b;
        super();
        this.config = config;
        this.handlers = handlers;
        this.API_VERSION = "api/v2";
        this.attachmentConstraints = null;
        if (notifyReady) {
            (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.ready) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
    }
    downloadFile(_file) {
        return Promise.reject(new Error("Method not implemented."));
    }
    reconnect(contact, conversationType) {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined;
        });
    }
    createChat(contact, conversationType) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const body = {
                organizationId: this.config.pcOrganizationId,
                deploymentId: this.config.pcDeploymentId,
                routingTarget: {
                    targetType: "QUEUE",
                    targetAddress: this.config.pcQueue,
                },
                memberInfo: {
                    displayName: contact.lastName
                        ? contact.firstName + " " + contact.lastName
                        : contact.firstName,
                    avatarImageUrl: this.config.customerAvatarUrl,
                    lastName: contact.lastName,
                    firstName: contact.firstName,
                    email: contact.email,
                    customFields: Object.assign(Object.assign({}, contact), { [WIDGET_REFERRER_ID]: window.origin, [WIDGET_CONVERSATION_TYPE]: conversationType, [WIDGET_MODE]: this.config.kioskMode
                            ? OriginModeEnum.kiosk
                            : OriginModeEnum.website }),
                },
            };
            try {
                (_b = (_a = this.handlers).starting) === null || _b === void 0 ? void 0 : _b.call(_a);
                const data = yield ky
                    .post("webchat/guest/conversations", {
                    prefixUrl: this.prefixUrl,
                    json: body,
                })
                    .json();
                const info = {
                    uri: data.eventStreamUri,
                    id: data.id,
                    jwt: data.jwt,
                    memberId: data.member.id,
                };
                (_d = (_c = this.handlers).started) === null || _d === void 0 ? void 0 : _d.call(_c, info);
                // JWT Token used for future requests
                // this.pureCloudJwtTokenMap.set(info.id, info.jwt);
                // Initiate WebSocket communication with PureCloud (only then will it queue the chat request)
                EventService.init(this, info.uri, this.handlers);
            }
            catch (ex) {
                this.handlers.error(codeError(ex, ErrorCode.GENESYS_API_ERROR));
                throw ex;
            }
        });
    }
    endChat() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.$conversation) {
                    yield ky
                        .delete(`webchat/guest/conversations/${this.$conversation.id}/members/${this.$conversation.memberId}`, {
                        prefixUrl: this.prefixUrl,
                        hooks: {
                            beforeRequest: [
                                (request) => request.headers.set("Authorization", `Bearer ${this.$conversation.jwt}`),
                            ],
                        },
                    })
                        .json();
                }
            }
            catch (ex) {
                this.handlers.error(codeError(ex, ErrorCode.GENESYS_API_ERROR));
                throw ex;
            }
        });
    }
    acknowledgeMessages() {
        return __awaiter(this, void 0, void 0, function* () { });
    }
    sendMessage(text, bodyType = "notice", conversation) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const conv = this.$conversation || conversation;
            const data = {
                body: text,
                bodyType,
            };
            try {
                yield ky
                    .post(`webchat/guest/conversations/${conv.id}/members/${conv.memberId}/messages`, {
                    prefixUrl: this.prefixUrl,
                    json: data,
                    hooks: {
                        beforeRequest: [
                            (request) => request.headers.set("Authorization", `Bearer ${conv.jwt}`),
                        ],
                    },
                })
                    .json();
            }
            catch (ex) {
                (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.error(codeError(ex, ErrorCode.GENESYS_API_ERROR));
            }
        });
    }
    sendTyping() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.$conversation) {
                return;
            }
            try {
                yield ky
                    .post(`webchat/guest/conversations/${this.$conversation.id}/members/${this.$conversation.memberId}/typing`, {
                    prefixUrl: this.prefixUrl,
                    hooks: {
                        beforeRequest: [
                            (request) => request.headers.set("Authorization", `Bearer ${this.$conversation.jwt}`),
                        ],
                    },
                })
                    .json();
            }
            catch (ex) {
                this.handlers.error(codeError(ex, ErrorCode.GENESYS_API_ERROR));
            }
        });
    }
    sendFile() {
        return __awaiter(this, void 0, void 0, function* () {
            // sending files in api is not supported for now
            // https://help.mypurecloud.com/articles/send-media-and-files-in-chat/
            return Promise.reject(new Error("not-implemented"));
        });
    }
    destroy() {
        EventService.destroy();
    }
    getMemberInfo(conversationId, memberId) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (memberId in this.$members) {
                return this.$members[memberId];
            }
            try {
                const member = yield ky
                    .get(`webchat/guest/conversations/${conversationId}/members/${memberId}`, {
                    prefixUrl: this.prefixUrl,
                    hooks: {
                        beforeRequest: [
                            (request) => request.headers.set("Authorization", `Bearer ${this.$conversation.jwt}`),
                        ],
                    },
                })
                    .json();
                log("retrieved member ", member);
                (_b = (_a = this.handlers).memberSet) === null || _b === void 0 ? void 0 : _b.call(_a, member);
                return member;
            }
            catch (ex) {
                this.handlers.error(codeError(ex, ErrorCode.GENESYS_API_ERROR));
            }
        });
    }
    customerConnected() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.$conversation)
                return false;
            try {
                const member = yield this.getMemberInfo(this.$conversation.id, this.$conversation.memberId);
                return member.state !== "DISCONNECTED";
            }
            catch (_a) {
                return false;
            }
        });
    }
}
//# sourceMappingURL=genesys.chat.api.service.js.map